import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Progress,
  Table,
  Badge,
  UncontrolledTooltip
} from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getProveedoresDashboard } from '../../helpers/backend_helper';

const ImportarPagos = () => {
  const [archivo, setArchivo] = useState(null);
  const [codigoProveedor, setCodigoProveedor] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [cargandoProveedores, setCargandoProveedores] = useState(false);
  const [formatoDetectado, setFormatoDetectado] = useState(null);

  // Cargar lista de proveedores al montar el componente
  useEffect(() => {
    setCargandoProveedores(true);
    getProveedoresDashboard()
      .then((response) => {
        if (response.data?.data) {
          setProveedores(response.data.data);
        }
        setCargandoProveedores(false);
      })
      .catch((err) => {
        console.error("Error al cargar proveedores:", err);
        setCargandoProveedores(false);
      });
  }, []);

  const handleArchivoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar extensión
      const extension = file.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv'].includes(extension)) {
        toast.error('Formato de archivo no válido. Use .xlsx, .xls o .csv');
        return;
      }
      setArchivo(file);
      setResultado(null);
      setError(null);
      setFormatoDetectado(null);
    }
  };

  const handleImportar = async () => {
    if (!archivo) {
      toast.error('Por favor seleccione un archivo');
      return;
    }

    // Si el formato detectado es finiquito, validar proveedor
    if (formatoDetectado === 'finiquito' && !codigoProveedor) {
      toast.error('Para archivos de finiquito es obligatorio seleccionar un proveedor');
      return;
    }

    setCargando(true);
    setError(null);
    setResultado(null);

    try {
      const formData = new FormData();
      formData.append('archivo', archivo);

      const params = new URLSearchParams();
      if (codigoProveedor) {
        params.append('proveedor', codigoProveedor);
      }
      params.append('dry_run', dryRun);

      const url = `${process.env.REACT_APP_API_URL}/api/v1/tracking-pago/importar?${params.toString()}`;

      const response = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResultado(response.data);
      setFormatoDetectado(response.data.formato);
      
      if (dryRun) {
        toast.info('Validación completada. Revise el reporte y ejecute sin dry-run para guardar.');
      } else {
        toast.success(`Importación completada: ${response.data.estadisticas.actualizadas} facturas actualizadas`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al importar archivo';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setCargando(false);
    }
  };

  const handleLimpiar = () => {
    setArchivo(null);
    setCodigoProveedor('');
    setDryRun(true);
    setResultado(null);
    setError(null);
    document.getElementById('archivo-input').value = '';
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#405189' }}>
                <h4 className="mb-0 text-white">
                  <i className="ri-file-upload-line me-2"></i>
                  Importar Tracking de Pagos desde Aseguradora
                </h4>
              </CardHeader>
              <CardBody>
                {/* Instrucciones */}
                <Alert color="info" className="mb-4">
                  <h5 className="alert-heading">
                    <i className="ri-information-line me-2"></i>
                    Instrucciones
                  </h5>
                  <ul className="mb-0">
                    <li>Seleccione el archivo Excel (.xlsx, .xls) o CSV con los pagos de la aseguradora</li>
                    <li>El sistema detecta automáticamente el formato: <strong>Pagos Estándar</strong> o <strong>Finiquito MyH+</strong></li>
                    <li>Para archivos de <strong>Finiquito</strong>, es obligatorio seleccionar un proveedor</li>
                    <li>Use <strong>Modo Dry-Run</strong> primero para validar sin guardar cambios</li>
                    <li>Revise el reporte de validación</li>
                    <li>Si todo está correcto, desactive <strong>Modo Dry-Run</strong> y haga clic en <strong>Importar Datos</strong></li>
                  </ul>
                </Alert>

                {/* Formulario */}
                <Form>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="archivo-input">
                          Archivo <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="archivo-input"
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={handleArchivoChange}
                          disabled={cargando}
                        />
                        {archivo && (
                          <div className="mt-2">
                            <Badge color="success" className="me-2">
                              <i className="ri-file-line me-1"></i>
                              {archivo.name}
                            </Badge>
                            <Badge color="info" className="me-2">
                              {(archivo.size / 1024).toFixed(2)} KB
                            </Badge>
                            {formatoDetectado && (
                              <Badge color={formatoDetectado === 'finiquito' ? 'warning' : 'primary'}>
                                <i className="ri-file-list-3-line me-1"></i>
                                {formatoDetectado === 'finiquito' ? 'Finiquito MyH+' : 'Pagos Estándar'}
                              </Badge>
                            )}
                          </div>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md={3}>
                      <FormGroup>
                        <Label for="proveedor-input">
                          Proveedor
                          {formatoDetectado === 'finiquito' && (
                            <span className="text-danger ms-1">*</span>
                          )}
                          <i 
                            className="ri-information-line ms-1 text-info" 
                            id="tooltip-proveedor"
                            style={{ cursor: 'pointer' }}
                          ></i>
                        </Label>
                        <UncontrolledTooltip placement="top" target="tooltip-proveedor">
                          {formatoDetectado === 'finiquito' 
                            ? 'Obligatorio para archivos de finiquito' 
                            : 'Opcional. Filtra solo facturas de este proveedor.'}
                        </UncontrolledTooltip>
                        <Input
                          id="proveedor-input"
                          type="select"
                          value={codigoProveedor}
                          onChange={(e) => setCodigoProveedor(e.target.value)}
                          disabled={cargando || cargandoProveedores}
                          required={formatoDetectado === 'finiquito'}
                        >
                          <option value="">Seleccione un proveedor</option>
                          {proveedores.map((prov) => (
                            <option key={prov.codigo} value={prov.codigo}>
                              {prov.nombre}
                            </option>
                          ))}
                        </Input>
                        {cargandoProveedores && (
                          <small className="text-muted">
                            <i className="ri-loader-4-line me-1"></i>
                            Cargando proveedores...
                          </small>
                        )}
                        {formatoDetectado === 'finiquito' && !codigoProveedor && (
                          <small className="text-danger">
                            <i className="ri-error-warning-line me-1"></i>
                            Requerido para finiquito
                          </small>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md={3}>
                      <FormGroup>
                        <Label>
                          <Input
                            type="checkbox"
                            checked={dryRun}
                            onChange={(e) => setDryRun(e.target.checked)}
                            disabled={cargando}
                          />{' '}
                          Modo Dry-Run
                          <i 
                            className="ri-information-line ms-1 text-info" 
                            id="tooltip-dryrun"
                            style={{ cursor: 'pointer' }}
                          ></i>
                        </Label>
                        <UncontrolledTooltip placement="top" target="tooltip-dryrun">
                          Valida sin guardar cambios. Recomendado para primera ejecución.
                        </UncontrolledTooltip>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Button
                        color={dryRun ? 'info' : 'success'}
                        onClick={handleImportar}
                        disabled={!archivo || cargando}
                        className="me-2"
                      >
                        {cargando ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Procesando...
                          </>
                        ) : (
                          <>
                            <i className={dryRun ? 'ri-search-line me-2' : 'ri-upload-cloud-line me-2'}></i>
                            {dryRun ? 'Validar Archivo' : 'Importar Datos'}
                          </>
                        )}
                      </Button>

                      <Button
                        color="secondary"
                        onClick={handleLimpiar}
                        disabled={cargando}
                      >
                        <i className="ri-refresh-line me-2"></i>
                        Limpiar
                      </Button>
                    </Col>
                  </Row>
                </Form>

                {/* Barra de progreso */}
                {cargando && (
                  <div className="mt-4">
                    <Progress animated color="primary" value={100} />
                    <p className="text-center mt-2 text-muted">
                      Procesando archivo... Por favor espere.
                    </p>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <Alert color="danger" className="mt-4">
                    <h5 className="alert-heading">
                      <i className="ri-error-warning-line me-2"></i>
                      Error
                    </h5>
                    <p className="mb-0">{error}</p>
                  </Alert>
                )}

                {/* Resultado */}
                {resultado && (
                  <div className="mt-4">
                    <Card className="border-success">
                      <CardHeader className="bg-success text-white">
                        <h5 className="mb-0">
                          <i className="ri-checkbox-circle-line me-2"></i>
                          Reporte de {resultado.dry_run ? 'Validación' : 'Importación'}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        {/* Estadísticas */}
                        <Row className="mb-3">
                          <Col md={2}>
                            <div className="text-center p-3 bg-light rounded">
                              <h3 className="mb-0">{resultado.estadisticas.total_lineas}</h3>
                              <p className="text-muted mb-0 small">Total Líneas</p>
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="text-center p-3 bg-light rounded">
                              <h3 className="mb-0 text-success">{resultado.estadisticas.actualizadas}</h3>
                              <p className="text-muted mb-0 small">
                                Actualizadas
                                {resultado.estadisticas.total_lineas > 0 && (
                                  <Badge color="success" className="ms-2">
                                    {((resultado.estadisticas.actualizadas / resultado.estadisticas.total_lineas) * 100).toFixed(1)}%
                                  </Badge>
                                )}
                              </p>
                            </div>
                          </Col>
                          <Col md={2}>
                            <div className="text-center p-3 bg-light rounded">
                              <h3 className="mb-0 text-warning">{resultado.estadisticas.duplicadas || 0}</h3>
                              <p className="text-muted mb-0 small">Duplicadas</p>
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="text-center p-3 bg-light rounded">
                              <h3 className="mb-0 text-warning">{resultado.estadisticas.no_encontradas}</h3>
                              <p className="text-muted mb-0 small">
                                No Encontradas
                                {resultado.estadisticas.total_lineas > 0 && (
                                  <Badge color="warning" className="ms-2">
                                    {((resultado.estadisticas.no_encontradas / resultado.estadisticas.total_lineas) * 100).toFixed(1)}%
                                  </Badge>
                                )}
                              </p>
                            </div>
                          </Col>
                          <Col md={2}>
                            <div className="text-center p-3 bg-light rounded">
                              <h3 className="mb-0 text-danger">{resultado.estadisticas.errores}</h3>
                              <p className="text-muted mb-0 small">Errores</p>
                            </div>
                          </Col>
                        </Row>

                        {/* Facturas duplicadas */}
                        {resultado.facturas_duplicadas && resultado.facturas_duplicadas.length > 0 && (
                          <div className="mt-3">
                            <Alert color="warning" className="mb-3">
                              <h6 className="alert-heading">
                                <i className="ri-alert-line me-2"></i>
                                Facturas Duplicadas Detectadas ({resultado.facturas_duplicadas.length})
                              </h6>
                              <p className="mb-0 small">
                                Se encontraron múltiples facturas con el mismo número. El sistema usó la primera encontrada, 
                                pero debe revisar manualmente cuál es la correcta.
                              </p>
                            </Alert>
                            
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              {resultado.facturas_duplicadas.map((dup, idx) => (
                                <Card key={idx} className="mb-3 border-warning">
                                  <CardHeader className="bg-warning bg-opacity-10">
                                    <Row className="align-items-center">
                                      <Col md={8}>
                                        <strong>Línea {dup.linea}:</strong> Factura <code>{dup.factura}</code> 
                                        <Badge color="warning" className="ms-2">{dup.cantidad} facturas encontradas</Badge>
                                      </Col>
                                      <Col md={4} className="text-end">
                                        <small className="text-muted">
                                          Proveedor: {dup.proveedor} | Siniestro: {dup.siniestro}
                                        </small>
                                      </Col>
                                    </Row>
                                  </CardHeader>
                                  <CardBody className="p-2">
                                    <div className="mb-2">
                                      <Badge color="info" className="me-2">
                                        <i className="ri-file-excel-line me-1"></i>
                                        Monto Excel: ${dup.monto_excel_usd?.toFixed(2) || '0.00'}
                                      </Badge>
                                    </div>
                                    
                                    <Table size="sm" bordered hover className="mb-0">
                                      <thead className="table-light">
                                        <tr>
                                          <th width="5%">#</th>
                                          <th width="10%">Solicitud</th>
                                          <th width="30%">Titular</th>
                                          <th width="12%">Monto Bs</th>
                                          <th width="12%">Monto USD</th>
                                          <th width="12%">Fecha</th>
                                          <th width="12%">Tracking</th>
                                          <th width="7%">Match</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {dup.facturas.map((f, fIdx) => {
                                          const diferencia = Math.abs((f.monto_usd || 0) - (dup.monto_excel_usd || 0));
                                          const esMatch = diferencia < 1.0; // Tolerancia de $1
                                          
                                          return (
                                            <tr key={fIdx} className={fIdx === 0 ? 'table-primary' : ''}>
                                              <td className="text-center">
                                                {fIdx === 0 && (
                                                  <Badge color="primary" pill>
                                                    Usada
                                                  </Badge>
                                                )}
                                              </td>
                                              <td>
                                                <code>{f.solicitud_id}</code>
                                              </td>
                                              <td>
                                                <small>{f.titular}</small>
                                              </td>
                                              <td className="text-end">
                                                {f.monto_bs?.toLocaleString('es-VE', { minimumFractionDigits: 2 })} Bs
                                              </td>
                                              <td className="text-end">
                                                <strong>${f.monto_usd?.toFixed(2) || '0.00'}</strong>
                                                {esMatch && (
                                                  <i 
                                                    className="ri-check-line text-success ms-1" 
                                                    id={`match-${idx}-${fIdx}`}
                                                  ></i>
                                                )}
                                                {esMatch && (
                                                  <UncontrolledTooltip target={`match-${idx}-${fIdx}`}>
                                                    Monto similar al Excel
                                                  </UncontrolledTooltip>
                                                )}
                                              </td>
                                              <td>
                                                <small>{f.fecha}</small>
                                              </td>
                                              <td className="text-center">
                                                {f.tiene_tracking ? (
                                                  <Badge color="success" className="badge-soft-success">
                                                    <i className="ri-checkbox-circle-line me-1"></i>
                                                    Sí
                                                  </Badge>
                                                ) : (
                                                  <Badge color="danger" className="badge-soft-danger">
                                                    <i className="ri-close-circle-line me-1"></i>
                                                    No
                                                  </Badge>
                                                )}
                                              </td>
                                              <td className="text-center">
                                                {esMatch ? (
                                                  <i className="ri-thumb-up-fill text-success fs-5"></i>
                                                ) : (
                                                  <i className="ri-thumb-down-fill text-muted fs-5"></i>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </Table>
                                    
                                    {dup.facturas.length > 1 && (
                                      <Alert color="info" className="mt-2 mb-0 small">
                                        <i className="ri-information-line me-1"></i>
                                        <strong>Acción requerida:</strong> Revise manualmente cuál factura es la correcta. 
                                        El sistema actualizó la primera ({dup.facturas[0].solicitud_id}).
                                      </Alert>
                                    )}
                                  </CardBody>
                                </Card>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Facturas no encontradas */}
                        {resultado.facturas_no_encontradas && resultado.facturas_no_encontradas.length > 0 && (
                          <div className="mt-3">
                            <h6 className="text-warning">
                              <i className="ri-alert-line me-2"></i>
                              Facturas No Encontradas ({resultado.facturas_no_encontradas.length})
                            </h6>
                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                              <Table size="sm" bordered hover>
                                <thead className="table-light">
                                  <tr>
                                    <th>Línea</th>
                                    <th>Código</th>
                                    <th>Nombre Proveedor</th>
                                    <th>Factura</th>
                                    <th>Fecha Factura</th>
                                    <th>Siniestro</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {resultado.facturas_no_encontradas.map((f, idx) => (
                                    <tr key={idx}>
                                      <td>{f.linea}</td>
                                      <td>{f.proveedor}</td>
                                      <td>{f.nombre_proveedor || 'N/A'}</td>
                                      <td><code>{f.factura}</code></td>
                                      <td>{f.fecha_factura || 'N/A'}</td>
                                      <td><small>{f.siniestro}</small></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}

                        {/* Errores */}
                        {resultado.errores_detalle && resultado.errores_detalle.length > 0 && (
                          <div className="mt-3">
                            <h6 className="text-danger">
                              <i className="ri-error-warning-line me-2"></i>
                              Errores Encontrados ({resultado.errores_detalle.length})
                            </h6>
                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                              <Table size="sm" bordered>
                                <thead>
                                  <tr>
                                    <th>Línea</th>
                                    <th>Error</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {resultado.errores_detalle.map((e, idx) => (
                                    <tr key={idx}>
                                      <td>{e.linea}</td>
                                      <td>{e.error}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}

                        {/* Acciones post-importación */}
                        <div className="mt-4 d-flex justify-content-between align-items-center">
                          <div>
                            {resultado.dry_run && resultado.estadisticas.errores === 0 && (
                              <Alert color="success" className="mb-0">
                                <i className="ri-checkbox-circle-line me-2"></i>
                                Validación exitosa. Puede proceder a importar desactivando el modo Dry-Run.
                              </Alert>
                            )}
                          </div>
                          <div>
                            <Button
                              color="secondary"
                              size="sm"
                              onClick={() => {
                                const dataStr = JSON.stringify(resultado, null, 2);
                                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                                const url = URL.createObjectURL(dataBlob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `reporte_importacion_${new Date().toISOString().split('T')[0]}.json`;
                                link.click();
                                toast.success('Reporte exportado');
                              }}
                            >
                              <i className="ri-download-line me-1"></i>
                              Exportar Reporte
                            </Button>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ImportarPagos;
